import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { FCM } from '@capacitor-community/fcm';
import { getToken, onMessage } from 'firebase/messaging';
import { Subject } from 'rxjs';
import { getMessagingInstance, vapidKey } from './firebase.config';

/**
 * Maps a notification's `data.type` (set by the backend) to the fixed route that should
 * be opened when the notification is tapped. Each notification type has a single,
 * predetermined audience/destination (e.g. "request_new" always targets the expert's
 * pending-requests screen) regardless of the recipient's full set of roles - a clinician
 * can hold both the expert and requester roles at once, so routing based on the current
 * user's role instead of the notification's own type would be wrong.
 */
const NOTIFICATION_TYPE_ROUTES: Record<string, string> = {
  request_new: '/dashboard/expert/pending-requests/details',
  request_taken_charge: '/dashboard/requester/request/details',
  request_opinion: '/dashboard/requester/request/details',
};

/**
 * Interface for FCM service state
 */
export interface FCMState {
  isInitialized: boolean;
  isInitializing: boolean;
  hasPermission: boolean;
  error: string | null;
}

/**
 * Service responsible for managing Firebase Cloud Messaging (FCM) functionality
 * Handles token registration, notification permissions, and message handling
 * across different platforms (web, Android, iOS)
 */
@Injectable({
  providedIn: 'root'
})
export class FCMService {
  private _httpClient = inject(HttpClient);
  private _router = inject(Router);

  /**
   * Subject emitting when a foreground message is received.
   * Useful for triggering automatic data refreshes across the app.
   */
  public readonly onMessageReceived$ = new Subject<any>();

  /**
   * Signal containing the current FCM token
   */
  public readonly token = signal<string | null>(null);

  /**
   * Signal containing the current FCM service state
   */
  public readonly state = signal<FCMState>({
    isInitialized: false,
    isInitializing: false,
    hasPermission: false,
    error: null
  });

  /**
   * Computed signal that indicates if FCM is ready to use
   */
  public readonly isReady = computed(() => {
    const currentState = this.state();
    const currentToken = this.token();
    return currentState.isInitialized && currentState.hasPermission && !!currentToken;
  });

  /**
   * Computed signal that indicates if there's an active error
   */
  public readonly hasError = computed(() => !!this.state().error);

  /**
   * Initialize FCM based on the current platform
   */
  async initializeFCM(): Promise<void> {
    console.log('[FCM] Initialization started...');

    this.state.update(state => ({
      ...state,
      isInitializing: true,
      error: null
    }));

    try {
      if (Capacitor.isNativePlatform()) {
        console.log('[FCM] Native platform detected:', Capacitor.getPlatform());
        await this._initializeNativePushNotifications();
      } else {
        console.log('[FCM] Web platform detected');
        await this._initializeWebPushNotifications();
      }

      this.state.update(state => ({
        ...state,
        isInitialized: true,
        isInitializing: false
      }));

      console.log('[FCM] Initialization completed successfully');

    } catch (error) {
      console.error('[FCM] Initialization failed:', error);

      this.state.update(state => ({
        ...state,
        isInitializing: false,
        error: error instanceof Error ? error.message : 'Unknown initialization error'
      }));
    }
  }

  /**
   * Initialize push notifications for native platforms (Android/iOS)
   */
  private async _initializeNativePushNotifications(): Promise<void> {
    console.log('[FCM] Initializing native notifications...');

    // Request notification permissions
    const permissionStatus = await PushNotifications.requestPermissions();
    console.log('[FCM] Permission status:', permissionStatus);

    if (permissionStatus.receive === 'granted') {
      this.state.update(state => ({
        ...state,
        hasPermission: true
      }));

      // Every listener must be attached before register() is called. register() triggers the
      // 'registration' event as soon as the platform hands back a token, and on a warm start that
      // can happen before the next line runs - the token would then be delivered to nobody and
      // never reach the backend, leaving that device silently unreachable.

      // Listen for successful token registration
      PushNotifications.addListener('registration', async (token) => {
        let fcmTokenValue = token.value;
        
        // On iOS, the token from PushNotifications is the APNs token.
        // We must fetch the FCM token using the community plugin.
        if (Capacitor.getPlatform() === 'ios') {
          try {
            const fcmResult = await FCM.getToken();
            fcmTokenValue = fcmResult.token;
            console.log('[FCM] iOS FCM token retrieved via plugin');
          } catch (err) {
            console.error('[FCM] Error getting FCM token on iOS', err);
          }
        }
        
        console.log('[FCM] Final Native token received:', fcmTokenValue);
        this.token.set(fcmTokenValue);
        this._sendTokenToBackend(fcmTokenValue);
      });

      // Listen for registration errors
      PushNotifications.addListener('registrationError', (error) => {
        console.error('[FCM] Native registration error:', error);
        this.state.update(state => ({
          ...state,
          error: 'Registration failed: ' + error.error
        }));
      });

      // Listen for incoming push notifications (foreground)
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[FCM] Native notification received:', notification);
        this.onMessageReceived$.next(notification);
        // Android and iOS deliver the payload but display nothing while the app is in the
        // foreground - the app has to raise the banner itself, otherwise a clinician already
        // using the app never sees that a request came in.
        this._showForegroundNotification(notification);
      });

      // Listen for notification interactions (app opened/resumed from a tapped notification -
      // this is the common case on iOS since the app is usually backgrounded when a push
      // arrives). Must also emit onMessageReceived$ so subscribers refresh their data,
      // otherwise the UI stays stale until the app is force-closed and reopened.
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('[FCM] Native notification action performed:', JSON.stringify(notification));
        this.onMessageReceived$.next(notification.notification);
        this._navigateFromNotificationData(notification.notification?.data);
      });

      // Taps on the banners we raise ourselves in the foreground: they are local notifications,
      // so they don't go through the push listener above.
      LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
        this._navigateFromNotificationData(event.notification?.extra);
      });

      // Now that nothing can be missed, ask the platform for a token.
      await PushNotifications.register();
      console.log('[FCM] Native registration completed');
    } else {
      console.warn('[FCM] Notification permissions denied');
      this.state.update(state => ({
        ...state,
        hasPermission: false,
        error: 'Notification permissions denied'
      }));
    }
  }

  /**
   * Initialize push notifications for web platform
   */
  private async _initializeWebPushNotifications(): Promise<void> {
    console.log('[FCM] Initializing web notifications...');

    try {
      // Get messaging instance - returns null if service workers are not available
      const messaging = getMessagingInstance();
      
      if (!messaging) {
        throw new Error('Firebase messaging is not available. Service workers may not be supported in this environment.');
      }

      // Request browser notification permission
      const permission = await Notification.requestPermission();
      console.log('[FCM] Browser permission:', permission);

      if (permission === 'granted') {
        this.state.update(state => ({
          ...state,
          hasPermission: true
        }));

        // Get FCM registration token using VAPID key
        const token = await getToken(messaging, {
          vapidKey: vapidKey
        });

        console.log('[FCM] Web token received:', token);
        this.token.set(token);
        this._sendTokenToBackend(token);

        // Listen for messages when app is in foreground
        onMessage(messaging, (payload) => {
          console.log('[FCM] Foreground message received:', payload);
          this.onMessageReceived$.next(payload);

          // Display custom notification for foreground messages
          if (payload.notification) {
            new Notification(payload.notification.title || 'New notification', {
              body: payload.notification.body,
              icon: payload.notification.icon || '/icons/icon-192x192.png'
            });
          }
        });
      } else {
        console.warn('[FCM] Browser notification permissions denied');
        this.state.update(state => ({
          ...state,
          hasPermission: false,
          error: 'Notification permissions denied'
        }));
      }
    } catch (error) {
      console.error('[FCM] Web initialization error:', error);
      throw error;
    }
  }

  /**
   * Send the FCM token to the backend API
   * Backend endpoint: POST /api/fcm
   * Uses 'aplt_backend' prefix to trigger auth interceptor
   * @param token - The FCM registration token
   */
  private async _sendTokenToBackend(token: string): Promise<void> {
    console.log('[FCM] Sending token to backend...');

    try {
      await this._httpClient.post('aplt_backend/api/fcm', { token }).toPromise();
      console.log('[FCM] Token registered successfully on backend');
    } catch (error) {
      console.error('[FCM] Failed to register token on backend:', error);
      this.state.update(state => ({
        ...state,
        error: 'Failed to register token with backend'
      }));
    }
  }

  /**
   * Navigate to the relevant screen based on the tapped notification's data payload.
   * The route is resolved from `data.type` alone (see NOTIFICATION_TYPE_ROUTES) -
   * not from the current user's role, since a clinician can hold both the expert and
   * requester roles simultaneously.
   */
  /**
   * Raises a local banner for a push received while the app is in the foreground.
   *
   * Both mobile platforms hand the payload straight to the app in that case and display nothing
   * of their own, so without this the notification is invisible to someone already using the app.
   * Tapping it routes exactly like a system notification.
   */
  private async _showForegroundNotification(notification: any): Promise<void> {
    const title = notification?.title;
    const body = notification?.body;

    // Data-only messages have nothing to show; the emitted event already refreshed the screens.
    if (!title && !body) {
      return;
    }

    try {
      const permission = await LocalNotifications.checkPermissions();
      if (permission.display !== 'granted') {
        const requested = await LocalNotifications.requestPermissions();
        if (requested.display !== 'granted') {
          return;
        }
      }

      await LocalNotifications.schedule({
        notifications: [
          {
            // Fits in a 32-bit int, which the Android scheduler requires.
            id: Date.now() % 2147483647,
            title: title || 'DipitaTouch',
            body: body || '',
            extra: notification?.data ?? {},
          },
        ],
      });
    } catch (error) {
      // Never let a failed banner break the message handling that follows.
      console.error('[FCM] Could not display foreground notification:', error);
    }
  }

  private _navigateFromNotificationData(data: Record<string, string> | undefined): void {
    const type = data?.['type'];
    const requestId = data?.['requestId'];
    const basePath = type && NOTIFICATION_TYPE_ROUTES[type];

    if (!basePath || !requestId) {
      return;
    }

    this._router.navigate([basePath, requestId]);
  }

  /**
   * Get the current FCM token
   */
  getCurrentToken(): string | null {
    return this.token();
  }

  /**
   * Force refresh of the FCM token
   */
  async refreshToken(): Promise<void> {
    console.log('[FCM] Refreshing token...');

    this.token.set(null);
    this.state.update(state => ({
      ...state,
      isInitialized: false,
      error: null
    }));

    await this.initializeFCM();
  }

  /**
   * Clear any existing error state
   */
  clearError(): void {
    this.state.update(state => ({
      ...state,
      error: null
    }));
  }

  /**
   * Check if the service has the necessary permissions
   */
  hasPermissions(): boolean {
    return this.state().hasPermission;
  }

  /**
   * Check if the service is currently initializing
   */
  isInitializing(): boolean {
    return this.state().isInitializing;
  }
}
